// Interactive-layer UI: a playing audio clip's progress, hover outline,
// selection frame and handles (or a connector's endpoint handles), attach
// targets, marquee, other people's cursors and selections, tool feedback,
// snap guides.
import type { BoardObject } from '../../shared/types.ts';
import { isColumn } from '../../shared/columns.ts';
import type { Editor } from '../editor.ts';
import { traceShape } from './draw/shape.ts';
import { drawAudioProgress } from './draw/audio.ts';
import { LOD_ZOOM } from './renderer.ts';
import { endpointPosition } from './connectors.ts';
import { HANDLE_SIZE, handlePoints, selectionFrame } from './selection.ts';

const ACCENT = '#4262FF';
const MARQUEE_FILL = 'rgba(66, 98, 255, 0.08)';
const GUIDE = '#FF3B8B';
const MAX_OUTLINES = 500;

/** Called with the world transform set. */
export function drawOverlay(ctx: CanvasRenderingContext2D, ed: Editor): void {
  const { store, renderer } = ed;
  const zoom = ed.camera.zoom;
  const px = 1 / zoom;
  const get = (id: string) => store.get(id);

  const outline = (o: BoardObject, width: number, color = ACCENT) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.beginPath();
    if (o.type === 'connector') {
      renderer.setWorldTransform(ctx);
      const pts = ed.connectors.shape(o).points;
      ctx.moveTo(pts[0]![0], pts[0]![1]);
      for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
      ctx.lineWidth = width + 2 * px;
    } else {
      renderer.setObjectTransform(ctx, o);
      if (o.type === 'shape') traceShape(ctx, o.kind, o.w, o.h);
      else ctx.rect(0, 0, o.w, o.h);
    }
    ctx.stroke();
    renderer.setWorldTransform(ctx);
  };

  // The clip that's playing, or paused part-way (spec §19.2), over its still image on the static layer.
  const clip = ed.audio.id ? store.get(ed.audio.id) : undefined;
  if (clip?.type === 'audio') drawAudioProgress(ctx, clip, ed.audio.fraction(), ed.audio.playing, zoom < LOD_ZOOM);

  if (ed.hovered && !ed.selection.has(ed.hovered) && !ed.transforming && ed.hovered !== ed.editingId) {
    const o = store.get(ed.hovered);
    if (o) outline(o, 2 * px);
  }
  if (ed.attachTarget) {
    const o = store.get(ed.attachTarget);
    if (o) outline(o, 2 * px);
  }

  if (ed.selection.size > 1 && ed.selection.size <= MAX_OUTLINES) {
    for (const id of ed.selection) {
      const o = store.get(id);
      if (o) outline(o, px);
    }
  }

  const single = ed.selection.size === 1 ? store.get(ed.selection.values().next().value!) : undefined;
  if (single?.type === 'connector') {
    // A connector shows its path and a handle at each end.
    outline(single, 1.5 * px);
    if (!ed.transforming && !ed.readOnly) {
      ctx.fillStyle = '#FFFFFF';
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1.5 * px;
      for (const end of ['from', 'to'] as const) {
        const [x, y] = endpointPosition(single, end, get);
        ctx.beginPath();
        ctx.arc(x, y, (HANDLE_SIZE / 2 + 1) * px, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
  } else {
    const frame = selectionFrame(store, ed.selection, (o) => ed.boundsOf(o));
    if (frame) {
      renderer.setObjectTransform(ctx, frame);
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1.5 * px;
      ctx.strokeRect(0, 0, frame.w, frame.h);
      if (!ed.transforming && !ed.editingId && !ed.readOnly) {
        const rotate = ed.selectedObjects().every((o) => o.type !== 'frame' && o.type !== 'connector');
        const hs = HANDLE_SIZE * px;
        ctx.fillStyle = '#FFFFFF';
        ctx.lineWidth = 1.5 * px;
        // A column's height follows its cards, so it only has side handles (spec §13.6).
        const sidesOnly = ed.selectedObjects().some(isColumn);
        for (const [handle, hx, hy] of handlePoints(frame, zoom, rotate)) {
          if (sidesOnly && handle !== 'e' && handle !== 'w') continue;
          ctx.beginPath();
          if (handle === 'rotate') ctx.arc(hx, hy, hs * 0.6, 0, Math.PI * 2);
          else ctx.rect(hx - hs / 2, hy - hs / 2, hs, hs);
          ctx.fill();
          ctx.stroke();
        }
      }
      renderer.setWorldTransform(ctx);
    }
  }

  if (ed.marquee) {
    const m = ed.marquee;
    ctx.fillStyle = MARQUEE_FILL;
    ctx.fillRect(m.x, m.y, m.w, m.h);
    ctx.strokeStyle = ACCENT;
    ctx.lineWidth = px;
    ctx.strokeRect(m.x, m.y, m.w, m.h);
  }

  if (ed.presence) {
    const animating = ed.presence.draw(ctx, zoom, store, (o, color) => outline(o, 2 * px, color));
    renderer.setWorldTransform(ctx);
    if (animating) renderer.invalidateInteractive();
  }

  ed.tool.drawOverlay?.(ctx);
  renderer.setWorldTransform(ctx);

  if (ed.guides.length) {
    ctx.strokeStyle = GUIDE;
    ctx.lineWidth = px;
    ctx.beginPath();
    for (const g of ed.guides) {
      ctx.moveTo(g.x1, g.y1);
      ctx.lineTo(g.x2, g.y2);
    }
    ctx.stroke();
  }
}
